import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const NotificationSettings = ({ settings, onSave }) => {
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: {
      newsletters: settings?.emailNotifications?.newsletters ?? true,
      eventUpdates: settings?.emailNotifications?.eventUpdates ?? true,
      communityActivity: settings?.emailNotifications?.communityActivity ?? false,
      marketingEmails: settings?.emailNotifications?.marketingEmails ?? true,
      weeklyDigest: settings?.emailNotifications?.weeklyDigest ?? true,
      achievementAlerts: settings?.emailNotifications?.achievementAlerts ?? true
    },
    pushNotifications: {
      goalReminders: settings?.pushNotifications?.goalReminders ?? true,
      eventReminders: settings?.pushNotifications?.eventReminders ?? true,
      communityMentions: settings?.pushNotifications?.communityMentions ?? true,
      newMessages: settings?.pushNotifications?.newMessages ?? true,
      milestoneAlerts: settings?.pushNotifications?.milestoneAlerts ?? true,
      dailyTips: settings?.pushNotifications?.dailyTips ?? false
    },
    smsNotifications: {
      urgentAlerts: settings?.smsNotifications?.urgentAlerts ?? false,
      appointmentReminders: settings?.smsNotifications?.appointmentReminders ?? true,
      emergencyContacts: settings?.smsNotifications?.emergencyContacts ?? true
    },
    frequency: {
      digestFrequency: settings?.frequency?.digestFrequency ?? 'weekly',
      reminderFrequency: settings?.frequency?.reminderFrequency ?? 'daily',
      quietHours: settings?.frequency?.quietHours ?? { start: '22:00', end: '08:00' }
    }
  });

  const [hasChanges, setHasChanges] = useState(false);

  const handleSettingChange = (category, setting, value) => {
    setNotificationSettings(prev => ({
      ...prev,
      [category]: {
        ...prev?.[category],
        [setting]: value
      }
    }));
    setHasChanges(true);
  };

  const handleFrequencyChange = (setting, value) => {
    setNotificationSettings(prev => ({
      ...prev,
      frequency: {
        ...prev?.frequency,
        [setting]: value
      }
    }));
    setHasChanges(true);
  };

  const handleSave = () => {
    onSave(notificationSettings);
    setHasChanges(false);
  };

  const handleReset = () => {
    setNotificationSettings({
      emailNotifications: {
        newsletters: settings?.emailNotifications?.newsletters ?? true,
        eventUpdates: settings?.emailNotifications?.eventUpdates ?? true,
        communityActivity: settings?.emailNotifications?.communityActivity ?? false,
        marketingEmails: settings?.emailNotifications?.marketingEmails ?? true,
        weeklyDigest: settings?.emailNotifications?.weeklyDigest ?? true,
        achievementAlerts: settings?.emailNotifications?.achievementAlerts ?? true
      },
      pushNotifications: {
        goalReminders: settings?.pushNotifications?.goalReminders ?? true,
        eventReminders: settings?.pushNotifications?.eventReminders ?? true,
        communityMentions: settings?.pushNotifications?.communityMentions ?? true,
        newMessages: settings?.pushNotifications?.newMessages ?? true,
        milestoneAlerts: settings?.pushNotifications?.milestoneAlerts ?? true,
        dailyTips: settings?.pushNotifications?.dailyTips ?? false
      },
      smsNotifications: {
        urgentAlerts: settings?.smsNotifications?.urgentAlerts ?? false,
        appointmentReminders: settings?.smsNotifications?.appointmentReminders ?? true,
        emergencyContacts: settings?.smsNotifications?.emergencyContacts ?? true
      },
      frequency: {
        digestFrequency: settings?.frequency?.digestFrequency ?? 'weekly',
        reminderFrequency: settings?.frequency?.reminderFrequency ?? 'daily',
        quietHours: settings?.frequency?.quietHours ?? { start: '22:00', end: '08:00' }
      }
    });
    setHasChanges(false);
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <Icon name="Bell" size={24} className="text-primary" />
          <h2 className="text-xl font-heading font-semibold text-foreground">Notification Settings</h2>
        </div>
        {hasChanges && (
          <div className="flex space-x-2">
            <Button variant="outline" onClick={handleReset}>
              Reset
            </Button>
            <Button variant="default" onClick={handleSave}>
              Save Changes
            </Button>
          </div>
        )}
      </div>
      <div className="space-y-8">
        {/* Email Notifications */}
        <div>
          <div className="flex items-center space-x-2 mb-4">
            <Icon name="Mail" size={20} className="text-primary" />
            <h3 className="text-lg font-heading font-medium text-foreground">Email Notifications</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ml-6">
            <Checkbox
              label="Newsletter & Updates"
              description="Monthly wellness newsletter and platform updates"
              checked={notificationSettings?.emailNotifications?.newsletters}
              onChange={(e) => handleSettingChange('emailNotifications', 'newsletters', e?.target?.checked)}
            />
            <Checkbox
              label="Event Updates"
              description="Notifications about upcoming events and workshops"
              checked={notificationSettings?.emailNotifications?.eventUpdates}
              onChange={(e) => handleSettingChange('emailNotifications', 'eventUpdates', e?.target?.checked)}
            />
            <Checkbox
              label="Community Activity"
              description="Updates on forum posts and community discussions"
              checked={notificationSettings?.emailNotifications?.communityActivity}
              onChange={(e) => handleSettingChange('emailNotifications', 'communityActivity', e?.target?.checked)}
            />
            <Checkbox
              label="Marketing Emails"
              description="Special offers and promotional content"
              checked={notificationSettings?.emailNotifications?.marketingEmails}
              onChange={(e) => handleSettingChange('emailNotifications', 'marketingEmails', e?.target?.checked)}
            />
            <Checkbox
              label="Weekly Digest"
              description="Summary of your weekly wellness progress"
              checked={notificationSettings?.emailNotifications?.weeklyDigest}
              onChange={(e) => handleSettingChange('emailNotifications', 'weeklyDigest', e?.target?.checked)}
            />
            <Checkbox
              label="Achievement Alerts"
              description="Notifications when you earn badges or reach milestones"
              checked={notificationSettings?.emailNotifications?.achievementAlerts}
              onChange={(e) => handleSettingChange('emailNotifications', 'achievementAlerts', e?.target?.checked)}
            />
          </div>
        </div>

        {/* Push Notifications */}
        <div>
          <div className="flex items-center space-x-2 mb-4">
            <Icon name="Smartphone" size={20} className="text-primary" />
            <h3 className="text-lg font-heading font-medium text-foreground">Push Notifications</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ml-6">
            <Checkbox
              label="Goal Reminders"
              description="Daily reminders to work on your wellness goals"
              checked={notificationSettings?.pushNotifications?.goalReminders}
              onChange={(e) => handleSettingChange('pushNotifications', 'goalReminders', e?.target?.checked)}
            />
            <Checkbox
              label="Event Reminders"
              description="Reminders before scheduled events and workshops"
              checked={notificationSettings?.pushNotifications?.eventReminders}
              onChange={(e) => handleSettingChange('pushNotifications', 'eventReminders', e?.target?.checked)}
            />
            <Checkbox
              label="Community Mentions"
              description="When someone mentions you in community discussions"
              checked={notificationSettings?.pushNotifications?.communityMentions}
              onChange={(e) => handleSettingChange('pushNotifications', 'communityMentions', e?.target?.checked)}
            />
            <Checkbox
              label="New Messages"
              description="Direct messages from other community members"
              checked={notificationSettings?.pushNotifications?.newMessages}
              onChange={(e) => handleSettingChange('pushNotifications', 'newMessages', e?.target?.checked)}
            />
            <Checkbox
              label="Milestone Alerts"
              description="Celebrate when you reach important milestones"
              checked={notificationSettings?.pushNotifications?.milestoneAlerts}
              onChange={(e) => handleSettingChange('pushNotifications', 'milestoneAlerts', e?.target?.checked)}
            />
            <Checkbox
              label="Daily Tips"
              description="Daily wellness tips and motivational messages"
              checked={notificationSettings?.pushNotifications?.dailyTips}
              onChange={(e) => handleSettingChange('pushNotifications', 'dailyTips', e?.target?.checked)}
            />
          </div>
        </div>

        {/* SMS Notifications */}
        <div>
          <div className="flex items-center space-x-2 mb-4">
            <Icon name="MessageSquare" size={20} className="text-primary" />
            <h3 className="text-lg font-heading font-medium text-foreground">SMS Notifications</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ml-6">
            <Checkbox
              label="Urgent Alerts"
              description="Critical system alerts and emergency notifications"
              checked={notificationSettings?.smsNotifications?.urgentAlerts}
              onChange={(e) => handleSettingChange('smsNotifications', 'urgentAlerts', e?.target?.checked)}
            />
            <Checkbox
              label="Appointment Reminders"
              description="Reminders for scheduled wellness appointments"
              checked={notificationSettings?.smsNotifications?.appointmentReminders}
              onChange={(e) => handleSettingChange('smsNotifications', 'appointmentReminders', e?.target?.checked)}
            />
            <Checkbox
              label="Emergency Contacts"
              description="Allow emergency contacts to receive wellness alerts"
              checked={notificationSettings?.smsNotifications?.emergencyContacts}
              onChange={(e) => handleSettingChange('smsNotifications', 'emergencyContacts', e?.target?.checked)}
            />
          </div>
        </div>

        {/* Notification Frequency */}
        <div>
          <div className="flex items-center space-x-2 mb-4">
            <Icon name="Clock" size={20} className="text-primary" />
            <h3 className="text-lg font-heading font-medium text-foreground">Notification Frequency</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 ml-6">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Digest Frequency
              </label>
              <select
                value={notificationSettings?.frequency?.digestFrequency}
                onChange={(e) => handleFrequencyChange('digestFrequency', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="never">Never</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Reminder Frequency
              </label>
              <select
                value={notificationSettings?.frequency?.reminderFrequency}
                onChange={(e) => handleFrequencyChange('reminderFrequency', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="hourly">Hourly</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="never">Never</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Quiet Hours
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="time"
                  value={notificationSettings?.frequency?.quietHours?.start}
                  onChange={(e) => handleFrequencyChange('quietHours', {
                    ...notificationSettings?.frequency?.quietHours,
                    start: e?.target?.value
                  })}
                  className="flex-1 px-2 py-2 border border-border rounded-lg bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring text-sm"
                />
                <span className="text-muted-foreground">to</span>
                <input
                  type="time"
                  value={notificationSettings?.frequency?.quietHours?.end}
                  onChange={(e) => handleFrequencyChange('quietHours', {
                    ...notificationSettings?.frequency?.quietHours,
                    end: e?.target?.value
                  })}
                  className="flex-1 px-2 py-2 border border-border rounded-lg bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring text-sm"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Quick Actions */}
      <div className="mt-8 pt-6 border-t border-border">
        <h4 className="font-heading font-medium text-foreground mb-4">Quick Actions</h4>
        <div className="flex flex-wrap gap-3">
          <Button
            variant="outline"
            onClick={() => {
              // Enable all essential notifications
              const essentialSettings = {
                ...notificationSettings,
                emailNotifications: {
                  ...notificationSettings?.emailNotifications,
                  newsletters: true,
                  eventUpdates: true,
                  achievementAlerts: true
                },
                pushNotifications: {
                  ...notificationSettings?.pushNotifications,
                  goalReminders: true,
                  eventReminders: true,
                  milestoneAlerts: true
                }
              };
              setNotificationSettings(essentialSettings);
              setHasChanges(true);
            }}
            iconName="CheckCircle"
            iconPosition="left"
          >
            Enable Essentials
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              // Disable all non-essential notifications
              const minimalSettings = {
                ...notificationSettings,
                emailNotifications: {
                  newsletters: false,
                  eventUpdates: true,
                  communityActivity: false,
                  marketingEmails: false,
                  weeklyDigest: false,
                  achievementAlerts: true
                },
                pushNotifications: {
                  goalReminders: true,
                  eventReminders: true,
                  communityMentions: false,
                  newMessages: true,
                  milestoneAlerts: true,
                  dailyTips: false
                }
              };
              setNotificationSettings(minimalSettings);
              setHasChanges(true);
            }}
            iconName="Minus"
            iconPosition="left"
          >
            Minimal Notifications
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;